/**
 * The answer kit — what every intent's answerer builds its reply with.
 *
 * One kit per turn. It owns the tool trace (the "Ran a → b" line), reports it
 * through `onProgress` as it grows, assembles the {@link OemAnswer}, and
 * collects the turn's **focus** (the machine / component / mine the answer
 * settled on) for the persona lens and the conversation memory.
 *
 * Also home to the small loaders and formatters more than one intent family
 * uses, so the families do not import each other.
 *
 * Engine layer — async data loads, otherwise pure.
 */

import { BY_ID, MINES } from '../../data/core.ts';
import type { MineCode } from '../../schema/machine.ts';
import { jobs } from '../oem-engine.ts';
import { routeForView } from '../oem-routes.ts';
import { loadOemDomain } from '../useOemData.ts';
import type { OemAnswer } from './answer-types.ts';
import type { Entities } from './entities.ts';
import { MEMORY_NOTE, PAGE_NOTE } from './page-context.ts';
import {
  loadWorkOrders,
  newToolContext,
  traceEntry,
  type OemTool,
  type OemToolContext,
} from './tools.ts';

/** Demo line 4082. Every local reply ends its trace with this. */
export const LOCAL_ENGINE = '(local engine)';

/** What an answer settled on — read by the persona lens and the memory. */
export interface AnswerFocus {
  machine?: string;
  component?: string;
  mine?: MineCode;
  alert?: string;
}

export interface AnswerKit {
  readonly toolCtx: OemToolContext;
  /** Run a tool, recording the call and reporting progress. */
  run<I, R>(tool: OemTool<I, R>, input: I): Promise<R>;
  /** Record a named query the answer ran inline (a seed or engine read). */
  ran(name: string, args?: Record<string, unknown>): void;
  /** Build the answer. The trace is closed with `(local engine)`. */
  done(
    markdown: string,
    sources: string[],
    extra?: Partial<Omit<OemAnswer, 'markdown' | 'sources' | 'tools'>>,
  ): OemAnswer;
  /** Set what the answer is about. Merges. */
  focus(f: AnswerFocus): void;
  readonly focused: AnswerFocus;
  /** Extra memory for the next turn (merged over the router's defaults). */
  remember(patch: Record<string, unknown>): void;
  readonly memo: Record<string, unknown>;
}

export function newAnswerKit(
  personaId: string,
  pathname: string,
  onProgress?: (trace: readonly string[]) => void,
): AnswerKit {
  const toolCtx = newToolContext(personaId, pathname);
  const focused: AnswerFocus = {};
  const memo: Record<string, unknown> = {};
  const report = () => onProgress?.([...toolCtx.trace]);
  return {
    toolCtx,
    async run(tool, input) {
      toolCtx.trace.push(traceEntry(tool.name, input as Record<string, unknown>));
      report();
      return tool.execute(input, toolCtx);
    },
    ran(name, args) {
      toolCtx.trace.push(traceEntry(name, args));
      report();
    },
    done(markdown, sources, extra) {
      return { markdown, sources, tools: [...toolCtx.trace, LOCAL_ENGINE], ...extra };
    },
    focus(f) {
      for (const [k, v] of Object.entries(f)) {
        if (v !== undefined) (focused as Record<string, unknown>)[k] = v;
      }
    },
    focused,
    remember(patch) {
      Object.assign(memo, patch);
    },
    memo,
  };
}

// ---------------------------------------------------------------------------
// Formatters shared across families
// ---------------------------------------------------------------------------

export const route = (view: string, sub?: string): string | undefined =>
  routeForView(view, sub) ?? undefined;

export const firstName = (s: string): string => s.split(' (')[0];

/**
 * A root-cause string cut back to its last whole clause — the ledger's
 * `root_cause` is truncated at 90 characters, mid-word.
 */
export const clause = (s: string): string =>
  /[.)]$/.test(s) || s.length < 80 || !s.includes(',') ? s.replace(/\.$/, '') : s.slice(0, s.lastIndexOf(','));

/** `Kalbarri` from `Kalbarri Gold` — the first word, as the demo printed sites. */
export const siteWord = (m: MineCode): string => MINES[m].name.split(' ')[0];

/**
 * The scope suffix the ported branches print: ` · L-02`, ` · Mount Isolde`.
 * An entity filled from the page or the conversation says so.
 */
export function scopeLabel(E: Entities, opts: { machine?: boolean; mine?: boolean } = {}): string {
  const note = (from: string | undefined) =>
    from === 'page' ? ` ${PAGE_NOTE}` : from === 'memory' ? ` ${MEMORY_NOTE}` : '';
  if (opts.machine !== false && E.machine) return ` · ${E.machine}${note(E.machineFrom)}`;
  if (opts.mine !== false && E.mine) return ` · ${MINES[E.mine].name}${note(E.mineFrom)}`;
  return '';
}

/** `L-02 (ML-21, Kalbarri)`. */
export const machineLabel = (id: string): string =>
  BY_ID[id] ? `${id} (${BY_ID[id].model}, ${siteWord(BY_ID[id].mine)})` : id;

// ---------------------------------------------------------------------------
// Loaders shared across families
// ---------------------------------------------------------------------------

/** `jobs()` needs three domains; several intents want it. */
export async function loadJobs() {
  const [workOrders, service] = await Promise.all([loadWorkOrders(), loadOemDomain('service')]);
  return jobs({ workOrders, techJobs: service.tapp_jobs, cases: service.cases });
}
